// pages/FavoritesPage.tsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { listFavoriteProducts, removeFavoriteProduct } from '../api/endpoints';
import type { FavoriteProduct, FavoriteListResponse } from '../types/api';
import useScanCredits from '../hooks/useScanCredits';
import { API_BASE_URL } from '../api/config';
import { postFavoriteListView } from '../api/telemetry';
import { ProductSearchModal } from '../components/ProductSearchModal';
import ConfirmationDialog from '../components/ui/ConfirmationDialog';
import { getErrorMessage } from '../utils/errors';
import {
  createProductReference,
  getProductRoute,
  type ProductPairTransfer,
} from '../utils/productIdentity';

/**
 * FavoritesPage — with daily-limit guard.
 * Selection bar renders in a PORTAL (document.body) so it's truly viewport-fixed.
 * Two-row layout in the bar: [count | Clear] then [Compatibility | Compare].
 */

// ── Icons ────────────────────────────────────────────────────
const SZ = { width: 18, height: 18 } as const;
const SZ_SM = { width: 16, height: 16 } as const;

const IconHeart = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 21s-6.4-4.35-9.2-8.06C1.02 11.66 1 8.65 3.2 6.4 5.4 4.2 8.7 4.1 10.9 6.3L12 7.41l1.1-1.1c2.2-2.2 5.5-2.1 7.7.1 2.2 2.24 2.18 5.26.2 6.54C18.4 16.65 12 21 12 21Z" />
  </svg>
);
const IconTrash = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18" />
    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    <path d="M10 11v6" /><path d="M14 11v6" />
    <path d="M5 6v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6" />
  </svg>
);
const IconSend = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="m4 4 16 8-16 8 5-8-5-8Z" />
  </svg>
);
const IconCompare = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 17h6" /><path d="M15 17h6" />
    <path d="M6 17V7" /><path d="M18 17V7" />
    <path d="M6 7 3 10" /><path d="M18 7 21 10" />
  </svg>
);
const IconDoc = () => (
  <svg viewBox="0 0 24 24" {...SZ} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 2h7l5 5v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z" />
    <path d="M13 2v5h5" />
  </svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24" {...SZ_SM} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="m20 6-11 11-5-5" />
  </svg>
);

// ── Utils ────────────────────────────────────────────────────
const normalizeImage = (url?: string | null) => {
  if (!url) return null;
  if (/^https?:/i.test(url)) return url;
  const base = API_BASE_URL.endsWith('/') ? API_BASE_URL : API_BASE_URL + '/';
  try { return new URL(url, base).toString(); } catch { return null; }
};

const fmtWhen = (iso: string | number | Date) => {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).format(d);
  } catch { return ''; }
};

// ── Types ────────────────────────────────────────────────────
type Selection = Record<number, FavoriteProduct>;

const createProductNavigationAction = (
  navigate: NavigateFunction,
  target: '/compatibility' | '/comparison',
  products: ProductPairTransfer['products'],
): (() => void) => () => {
  navigate(getProductRoute(target, products), { state: { products } satisfies ProductPairTransfer });
};

// ── Page ─────────────────────────────────────────────────────
export default function FavoritesPage(): React.JSX.Element {
  const nav = useNavigate();
  const [items, setItems] = useState<FavoriteProduct[]>([]);
  const [meta, setMeta] = useState<{ total: number; offset: number; limit: number; next?: number | null }>({ total: 0, offset: 0, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectionMode, setSelectionMode] = useState(false);
  const [selection, setSelection] = useState<Selection>({});
  const [hint, setHint] = useState<string | null>(null);
  const [searchingProduct, setSearchingProduct] = useState<{ keyword: string; title: string } | null>(null);
  const [confirmAction, setConfirmAction] = useState<(() => void) | null>(null);

  const hintTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Credit gating (cost comes from the API via the user context — never hardcoded).
  const { total: scansLeft, outOfQuota } = useScanCredits('product_barcode');

  const load = useCallback(async (offset = 0, append = false) => {
    if (append) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const res: FavoriteListResponse = await listFavoriteProducts({ limit: meta.limit, offset });
      setMeta({ total: res.total, offset: res.offset, limit: res.limit, next: res.next_offset ?? null });
      setItems((prev) => (append ? [...prev, ...res.items] : res.items));
    } catch (error_: unknown) {
      setError(getErrorMessage(error_, 'Failed to load favorites'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [meta.limit]);

  useEffect(() => {
    void load(0, false);
    const hasTracked = sessionStorage.getItem('telemetry_favorites_list_viewed');
    if (!hasTracked) {
      void postFavoriteListView().catch(() => { /* telemetry non-fatal */ });
      sessionStorage.setItem('telemetry_favorites_list_viewed', '1');
    }
  }, [load]);

  const selectedItems = useMemo(() => Object.values(selection), [selection]);
  // Barcode-less (slug-only) products are fine: compat/compare screens submit
  // product_id when the barcode is empty.
  const canPairSelected = useMemo(() => selectedItems.length === 2, [selectedItems]);

  const showHint = useCallback((msg: string) => {
    setHint(msg);
    if (hintTimeout.current) clearTimeout(hintTimeout.current);
    hintTimeout.current = setTimeout(() => { setHint(null); }, 1800);
  }, []);

  const toggleSelection = useCallback((product: FavoriteProduct) => {
    setSelection((prev) => {
      if (product.product_id in prev) {
        const { [product.product_id]: _removed, ...rest } = prev;
        return rest;
      }
      if (Object.keys(prev).length >= 2) {
        showHint('You can select up to 2 products.');
        return prev;
      }
      return { ...prev, [product.product_id]: product };
    });
  }, [showHint]);

  const enterSelectionWith = (p: FavoriteProduct) => {
    setSelectionMode(true);
    setSelection({ [p.product_id]: p });
  };
  const clearSelection = () => { setSelection({}); setSelectionMode(false); };

  const handleRemove = async (productId: number) => {
    const backup = items;
    setItems((prev) => prev.filter((i) => i.product_id !== productId));
    setSelection((prev) => {
      if (!(productId in prev)) return prev;
      const { [productId]: _removed, ...rest } = prev;
      return rest;
    });
    try { await removeFavoriteProduct(productId); } catch { setItems(backup); }
  };

  const makeAnalysisAction = useCallback((p: FavoriteProduct): () => void => {
    return () => {
      nav(`/scanner/result/${p.product_id}`, { state: { barcode: p.barcode ?? '', source: 'db' } });
    };
  }, [nav]);

  const openDetails = (id: number) => { nav(`/favorites/${id}`); };
  const sendToAnalysis = (p: FavoriteProduct) => {
    setConfirmAction(() => makeAnalysisAction(p));
  };

  const guardAndGo = (
    target: '/compatibility' | '/comparison',
    products: ProductPairTransfer['products'],
  ) => {
    if (outOfQuota) {
      showHint('Out of credits — top up or wait for your monthly reset.');
      return;
    }
    setConfirmAction(() => createProductNavigationAction(nav, target, products));
  };

  const sendSelection = (mode: 'compatibility' | 'comparison') => {
    if (!canPairSelected) return;
    const [a, b] = selectedItems;
    const target = mode === 'compatibility' ? '/compatibility' : '/comparison';
    guardAndGo(target, [createProductReference(a), createProductReference(b)]);
  };

  return (
    <div
      className="stack-5"
      style={{
        maxWidth: '860px',
        marginInline: 'auto',
        paddingBottom: selectedItems.length > 0
          ? 'calc(64px + env(safe-area-inset-bottom, 0px) + var(--s-9))'
          : 'var(--s-7)',
      }}
    >
      {/* Header card */}
      <div className="card card--bordered card--xl" style={{ padding: 'var(--s-5)' }}>
        <div className="row-4" style={{ justifyContent: 'space-between' }}>
          <div className="concern-dot concern-dot--teal">
            <IconHeart />
          </div>
          {!selectionMode ? (
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => { setSelectionMode(true); }}>
              Select
            </button>
          ) : (
            <button type="button" className="btn btn--ghost btn--sm" onClick={clearSelection}>
              Done
            </button>
          )}
        </div>

        <div className="stack-2" style={{ marginTop: 'var(--s-3)' }}>
          <h1 className="t-h3" style={{ margin: 0 }}>Favorite products</h1>
          <p className="t-small t-mute" style={{ margin: 0 }}>
            Tap a card to view details. Long-press to select up to two for comparison or compatibility.
          </p>
        </div>

        {hint && (
          <div className="notice notice--warn" style={{ marginTop: 'var(--s-3)' }}>
            {hint}
          </div>
        )}
      </div>

      {/* Loading / Error */}
      {loading && (
        <div className="card card--bordered" style={{ padding: 'var(--s-5)' }}>
          <p className="t-small t-mute" style={{ margin: 0 }}>Loading favorites…</p>
        </div>
      )}
      {error && <div className="notice notice--error">{error}</div>}

      {/* List */}
      {!loading && !error && (
        <div className="stack-4">
          {items.map((item) => (
            <FavoriteCard
              key={item.product_id}
              item={item}
              selectionMode={selectionMode}
              selected={!!selection[item.product_id]}
              outOfQuota={scansLeft !== null && scansLeft <= 0}
              onTap={() => {
                if (selectionMode) toggleSelection(item);
                else openDetails(item.product_id);
              }}
              onLongPress={() => {
                if (selectionMode) toggleSelection(item);
                else enterSelectionWith(item);
              }}
              onAnalyze={() => { sendToAnalysis(item); }}
              onRemove={() => handleRemove(item.product_id)}
              onDetails={() => { openDetails(item.product_id); }}
              onCompatibility={() =>
                { guardAndGo('/compatibility', [createProductReference(item)]); }
              }
              onComparison={() =>
                { guardAndGo('/comparison', [createProductReference(item)]); }
              }
              onBlocked={() => { showHint('Out of credits — top up or wait for your monthly reset.'); }}
              onSearch={() => { setSearchingProduct({ keyword: `${item.brand} ${item.name}`, title: item.name }); }}
            />
          ))}

          {items.length === 0 && (
            <div
              className="card card--bordered"
              style={{
                borderStyle: 'dashed',
                padding: 'var(--s-5) var(--s-4)',
                textAlign: 'center',
                background: 'var(--c-cream-weak)',
              }}
            >
              <div
                className="concern-dot concern-dot--teal"
                style={{ margin: '0 auto var(--s-3)' }}
              >
                <IconHeart />
              </div>
              <p className="t-small" style={{ margin: '0 0 var(--s-1)', fontWeight: 600, color: 'var(--ink)' }}>
                No saved products yet
              </p>
              <p className="t-small t-mute" style={{ margin: '0 auto', maxWidth: '36ch' }}>
                Add products from any analysis result to build your shortlist.
              </p>
            </div>
          )}
        </div>
      )}

      {meta.next != null && (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => { void load(meta.next ?? 0, true); }}
            disabled={loadingMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {/* Floating Actions via PORTAL */}
      {selectedItems.length > 0 &&
        createPortal(
          <FloatingSelectionBar
            count={selectedItems.length}
            canPairSelected={canPairSelected}
            outOfQuota={outOfQuota}
            onClear={clearSelection}
            onSendCompat={() => { sendSelection('compatibility'); }}
            onSendCompare={() => { sendSelection('comparison'); }}
            onBlocked={() => { showHint('Out of credits — top up or wait for your monthly reset.'); }}
          />,
          document.body
        )}

      {searchingProduct && (
        <ProductSearchModal
          keyword={searchingProduct.keyword}
          titleOverride={searchingProduct.title}
          onClose={() => { setSearchingProduct(null); }}
        />
      )}

      <ConfirmationDialog
        open={!!confirmAction}
        onClose={() => { setConfirmAction(null); }}
        onConfirm={() => {
          if (confirmAction) confirmAction();
          setConfirmAction(null);
        }}
        title="Confirm scan"
        description="This will use one scan from your daily quota. Proceed?"
        confirmLabel="Proceed"
        cancelLabel="Cancel"
      />
    </div>
  );
}

// ── Floating selection bar (portal-rendered) ─────────────────
function FloatingSelectionBar({
  count, canPairSelected, outOfQuota, onClear, onSendCompat, onSendCompare, onBlocked,
}: {
  count: number;
  canPairSelected: boolean;
  outOfQuota: boolean;
  onClear: () => void;
  onSendCompat: () => void;
  onSendCompare: () => void;
  onBlocked: () => void;
}) {
  const NAV_H = 64;
  const clickCompat = () => {
    if (outOfQuota) onBlocked();
    else onSendCompat();
  };
  const clickCompare = () => {
    if (outOfQuota) onBlocked();
    else onSendCompare();
  };

  return (
    <div
      className="fav-bar"
      style={{ bottom: `calc(${NAV_H}px + env(safe-area-inset-bottom, 0px) + var(--s-2))` }}
      aria-live="polite"
    >
      <div className="fav-bar__inner">
        <div className="row-3" style={{ justifyContent: 'space-between' }}>
          <span className="t-small t-deep" style={{ fontWeight: 500 }}>{count} selected</span>
          <button
            type="button"
            className="t-small t-deep"
            style={{ background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: 0, font: 'inherit' }}
            onClick={onClear}
          >
            Clear
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--s-2)' }}>
          <button
            type="button"
            className="btn btn--primary btn--sm btn--full"
            onClick={clickCompat}
            disabled={!canPairSelected || outOfQuota}
            title={outOfQuota ? 'Out of credits' : 'Send to compatibility'}
          >
            <IconSend />Compatibility
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm btn--full"
            onClick={clickCompare}
            disabled={!canPairSelected || outOfQuota}
            title={outOfQuota ? 'Out of credits' : 'Compare selected'}
          >
            <IconCompare />Compare
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Favorite card ─────────────────────────────────────────────
type FavoriteCardProps = {
  item: FavoriteProduct;
  selectionMode: boolean;
  selected: boolean;
  outOfQuota: boolean;
  onTap: () => void;
  onLongPress: () => void;
  onAnalyze: () => void;
  onRemove: () => void;
  onDetails: () => void;
  onCompatibility: () => void;
  onComparison: () => void;
  onBlocked: () => void;
  onSearch: () => void;
};

function FavoriteCard({
  item, selectionMode, selected, outOfQuota,
  onTap, onLongPress, onAnalyze, onRemove, onDetails, onCompatibility, onComparison, onBlocked, onSearch,
}: FavoriteCardProps) {
  const image = normalizeImage(item.image_url);
  const when = fmtWhen(item.favorited_at);

  const pressTimer = useRef<number | null>(null);
  const onPointerDown = () => {
    if (pressTimer.current) window.clearTimeout(pressTimer.current);
    pressTimer.current = window.setTimeout(() => { onLongPress(); }, 350);
  };
  const cancelTimer = () => { if (pressTimer.current) window.clearTimeout(pressTimer.current); };

  const compatClick = () => {
    if (outOfQuota) onBlocked();
    else onCompatibility();
  };
  const compareClick = () => {
    if (outOfQuota) onBlocked();
    else onComparison();
  };

  const handleActivate = () => {
    cancelTimer();
    onTap();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleActivate();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      onPointerDown={onPointerDown}
      onPointerUp={cancelTimer}
      onPointerLeave={cancelTimer}
      onContextMenu={(e) => { e.preventDefault(); }}
      className="card card--bordered card--xl"
      style={{
        position: 'relative',
        padding: 'var(--s-4)',
        cursor: 'pointer',
        transition: `box-shadow var(--t-fast) var(--ease)`,
        boxShadow: selected
          ? `0 0 0 2px var(--c-kivo-teal), var(--sh-soft)`
          : 'var(--sh-soft)',
      }}
      aria-pressed={selected}
    >
      {(selectionMode || selected) && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onTap(); }}
          className={`fav-bubble${selected ? ' fav-bubble--selected' : ''}`}
          aria-label={selected ? 'Deselect' : 'Select'}
        >
          {selected ? <IconCheck /> : null}
        </button>
      )}

      {/* thumb + meta */}
      <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 'var(--s-3)', minWidth: 0 }}>

        {/* Thumbnail */}
        {image ? (
          <div
            className="frame"
            style={{ width: 80, height: 80, display: 'grid', placeItems: 'center', flexShrink: 0 }}
          >
            <img
              loading="lazy"
              src={image}
              alt={item.name || 'Product'}
              style={{ maxHeight: 70, maxWidth: 60, objectFit: 'contain', display: 'block' }}
            />
          </div>
        ) : (
          <div
            style={{
              width: 80, height: 80,
              borderRadius: 'var(--r-sm)',
              background: 'var(--c-sand)',
              border: '1px dashed var(--rule)',
              display: 'grid', placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <span className="t-small t-mute">No image</span>
          </div>
        )}

        {/* Meta */}
        <div className="stack-2" style={{ minWidth: 0, gap: 'var(--s-1)' }}>
          <div
            className="t-small"
            style={{ fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {item.name}
          </div>
          <div className="t-small t-mute" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.brand}
          </div>
          <div className="t-small t-mute" style={{ wordBreak: 'break-all' }}>
            Barcode {item.barcode ?? '—'}
          </div>
          {when && (
            <div className="t-label" style={{ marginTop: 'var(--s-1)' }}>
              Saved {when}
            </div>
          )}
        </div>

        {/* Actions row — spans both columns */}
        <div
          style={{ gridColumn: '1 / -1', marginTop: 'var(--s-3)' }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'var(--s-2)' }}>

            {/* Actions dropdown */}
            <details className="fav-dropdown">
              <summary
                onClick={(e) => { e.stopPropagation(); }}
                className="btn btn--ghost btn--sm fav-dropdown__trigger"
                aria-haspopup="menu"
              >
                Actions
              <svg aria-hidden width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                <path d="m6 9 6 6 6-6"/>
              </svg>
              </summary>
              <div className="card card--bordered fav-dropdown__menu">
                <button type="button" className="fav-dropdown__item" onClick={(e) => { e.stopPropagation(); onAnalyze(); }}>
                  Analyze
                </button>
                <button type="button" className="fav-dropdown__item fav-dropdown__item--teal" onClick={(e) => { e.stopPropagation(); onSearch(); }}>
                  Search online
                </button>
                <button type="button" className="fav-dropdown__item" onClick={(e) => { e.stopPropagation(); compatClick(); }} disabled={outOfQuota}>
                  Compatibility
                </button>
                <button type="button" className="fav-dropdown__item" onClick={(e) => { e.stopPropagation(); compareClick(); }} disabled={outOfQuota}>
                  Comparison
                </button>
              </div>
            </details>

            {/* Details */}
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              style={{ justifyContent: 'center', gap: 'var(--s-1)' }}
              onClick={(e) => { e.stopPropagation(); onDetails(); }}
              title="Details"
            >
              <IconDoc />Details
            </button>

            {/* Remove */}
            <button
              type="button"
              className="btn btn--sm fav-remove"
              style={{ justifyContent: 'center', gap: 'var(--s-1)' }}
              onClick={(e) => { e.stopPropagation(); onRemove(); }}
              title="Remove from favorites"
            >
              <IconTrash />Remove
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
